/**
 * Node name generation utilities.
 * Used by SKU onboarding flow for creating AGX nodes.
 */

/**
 * Generates a node name following the convention: {location}-agx{number}
 *
 * @param {string} locationTitle - The location name (e.g., "University 310")
 * @param {number} index - The array index (0-based), used as base for numbering
 * @return {string} Generated node name (e.g., "university310-agx1")
 */
export const generateNodeName = (
  locationTitle: string,
  index: number
): string => {
  return `${locationTitle.replace(/\s+/g, "").toLowerCase()}-agx${index + 1}`;
};

/**
 * Resolves node name collisions by incrementing the suffix number.
 *
 * @param {string[]} names - Array of raw node names to check
 * @param {Set<string>} existingNames - Set of existing node names (case-insensitive comparison)
 * @return {string[]} Array of resolved node names with no collisions
 */
export const resolveNodeNameConflicts = (
  names: string[],
  existingNames: Set<string>
): string[] => {
  const taken = new Set(Array.from(existingNames).map(n => n.toLowerCase()));
  const result: string[] = [];

  for (const name of names) {
    // Match node name format: {location}-agx{number}
    const match = name.match(/^(.*-agx)(\d+)$/);

    if (!match) {
      result.push(name);

      taken.add(name.toLowerCase());
      continue;
    }

    const base = match[1]; // e.g., "erewhon-agx"
    let num = parseInt(match[2], 10);

    while (taken.has(`${base}${num}`.toLowerCase())) {
      num++;
    }

    const final = `${base}${num}`;

    result.push(final);

    taken.add(final.toLowerCase());
  }

  return result;
};
